import { Box, Divider, Skeleton, Typography } from '@mui/material';
import { FC, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { getVatAndEASFeeRequest } from 'redux/reducers/orders/reducer';
import {
	getConfirmedOrders,
	getConfirmedOrdersForFilterId,
	getConfirmedOrdersForFilterIdLoader,
	getConfirmedOrdersLoader,
	getVatAndEASFee,
	getVatAndEASFeeLoader,
} from 'redux/reducers/orders/selectors';
import { AmountAndCurrency } from 'ui/AmountAndCurrency';
import { DateTime } from 'luxon';
import { InfoDialButton } from 'ui/InfoDialButton';

const now = DateTime.now();

export const OrdersSummary: FC = () => {
	const dispatch = useDispatch();
	const orders = useSelector(getConfirmedOrders);
	const ordersIsLoading = useSelector(getConfirmedOrdersLoader);

	const ordersFilterId = useSelector(getConfirmedOrdersForFilterId);
	const ordersFilterIdLoading = useSelector(getConfirmedOrdersForFilterIdLoader);

	const vatAndEasFee = useSelector(getVatAndEASFee);
	const vatAndEasFeeIsLoading = useSelector(getVatAndEASFeeLoader);

	useEffect(() => {
		dispatch(getVatAndEASFeeRequest());
	}, [dispatch]);

	return (
		<Box>
			{!ordersIsLoading && !vatAndEasFeeIsLoading && !ordersFilterIdLoading ? (
				<Box
					sx={{
						display: 'flex',
						justifyContent: 'space-between',
						alignItems: 'space-between',
						gap: 1,
					}}
				>
					<Box
						sx={{
							display: 'flex',
							flexDirection: 'column',
							justifyContent: 'space-between',
							width: '100%',
						}}
					>
						<Typography sx={{ fontSize: '0.75rem' }} color="primary" gutterBottom>
							Order count
						</Typography>
						<Box
							sx={{
								display: 'flex',
								flexDirection: 'row',
								gap: 1,
								alignItems: 'end',
								justifyContent: 'space-between',
								width: '100%',
								mb: '-5px',
							}}
						>
							<Typography
								sx={{ fontSize: '1.5rem', fontWeight: 'bold', m: 0 }}
								gutterBottom
								color="primary"
							>
								{(orders?.totalCount ?? 0) + (ordersFilterId?.totalCount ?? 0)}
							</Typography>
						</Box>
					</Box>
					<Divider orientation="vertical" flexItem />
					<Box
						sx={{
							display: 'flex',
							flexDirection: 'column',
							justifyContent: 'space-between',
							width: '100%',
						}}
					>
						<Typography sx={{ fontSize: '0.75rem' }} color="primary" gutterBottom>
							Total order value
						</Typography>
						<Box
							sx={{
								display: 'flex',
								flexDirection: 'row',
								gap: 1,
								alignItems: 'end',
								justifyContent: 'space-between',
								width: '100%',
								mb: '-5px',
							}}
						>
							<AmountAndCurrency
								amount={
									(orders?.totalOrderValueInEur ?? 0) + (ordersFilterId?.totalOrderValueInEur ?? 0)
								}
								currency="EUR"
								size={1.5}
								color="primary"
								bold
							/>
						</Box>
					</Box>
					<Divider orientation="vertical" flexItem />
					<Box
						sx={{
							display: 'flex',
							flexDirection: 'column',
							justifyContent: 'space-between',
							width: '100%',
						}}
					>
						<Typography sx={{ fontSize: '0.75rem' }} color="primary" gutterBottom>
							Total paid orders
						</Typography>
						<Box
							sx={{
								display: 'flex',
								flexDirection: 'row',
								gap: 1,
								alignItems: 'end',
								justifyContent: 'space-between',
								width: '100%',
								mb: '-5px',
							}}
						>
							<AmountAndCurrency
								amount={
									(orders?.totalPaidOrdersAmount ?? 0) +
									(ordersFilterId?.totalPaidOrdersAmount ?? 0)
								}
								currency="EUR"
								size={1.5}
								color="primary"
								bold
							/>
						</Box>
					</Box>
					<Divider orientation="vertical" flexItem />
					<Box
						sx={{
							display: 'flex',
							flexDirection: 'column',
							justifyContent: 'space-between',
							width: '100%',
						}}
					>
						<Typography sx={{ fontSize: '0.75rem' }} color="primary" gutterBottom>
							Expected VAT amount (IOSS VAT scheme)
						</Typography>
						<Box
							sx={{
								display: 'flex',
								flexDirection: 'row',
								gap: 1,
								alignItems: 'end',
								justifyContent: 'space-between',
								width: '100%',
								mb: '-5px',
							}}
						>
							<AmountAndCurrency
								amount={vatAndEasFee?.iossImport ?? 0}
								currency="EUR"
								size={1.5}
								color="primary"
								bold
							/>
							<InfoDialButton
								text={`This is expected VAT amount to be payed to the Authorities for IOSS sales at the end of ${now
									.plus({ month: 1 })
									.toFormat('MMMM yyyy')}.`}
							/>
						</Box>
					</Box>
					<Divider orientation="vertical" flexItem />
					<Box
						sx={{
							display: 'flex',
							flexDirection: 'column',
							justifyContent: 'space-between',
							width: '100%',
						}}
					>
						<Typography sx={{ fontSize: '0.75rem' }} color="primary" gutterBottom>
							Expected VAT amount (Union OSS VAT scheme)
						</Typography>
						<Box
							sx={{
								display: 'flex',
								flexDirection: 'row',
								gap: 1,
								alignItems: 'end',
								justifyContent: 'space-between',
								width: '100%',
								mb: '-5px',
							}}
						>
							<AmountAndCurrency
								amount={vatAndEasFee?.iossUnion ?? 0}
								currency="EUR"
								size={1.5}
								color="primary"
								bold
							/>
							<InfoDialButton
								text={`This is expected VAT amount to be payed to the Authorities for Non Union OSS sales at the end of ${now
									.set({ month: now.quarter * 3 + 1, day: 1 })
									.toFormat('MMMM yyyy')}.`}
							/>
						</Box>
					</Box>
					<Divider orientation="vertical" flexItem />
					<Box
						sx={{
							display: 'flex',
							flexDirection: 'column',
							justifyContent: 'space-between',
							width: '100%',
						}}
					>
						<Typography sx={{ fontSize: '0.75rem' }} color="primary" gutterBottom>
							Expected VAT amount (Non Union OSS VAT scheme)
						</Typography>
						<Box
							sx={{
								display: 'flex',
								flexDirection: 'row',
								gap: 1,
								alignItems: 'end',
								justifyContent: 'space-between',
								width: '100%',
								mb: '-5px',
							}}
						>
							<AmountAndCurrency
								amount={vatAndEasFee?.iossNonUnion ?? 0}
								currency="EUR"
								size={1.5}
								color="primary"
								bold
							/>
							<InfoDialButton
								text={`This is expected VAT amount to be payed to the Authorities for Non Union OSS sales at the end of ${now
									.set({ month: now.quarter * 3 + 1, day: 1 })
									.toFormat('MMMM yyyy')}.`}
							/>
						</Box>
					</Box>
					<Divider orientation="vertical" flexItem />
					<Box
						sx={{
							display: 'flex',
							flexDirection: 'column',
							justifyContent: 'space-between',
							width: '100%',
						}}
					>
						<Typography sx={{ fontSize: '0.75rem' }} color="primary" gutterBottom>
							Expected EAS fee to be paid
						</Typography>
						<Box
							sx={{
								display: 'flex',
								flexDirection: 'row',
								gap: 1,
								alignItems: 'end',
								justifyContent: 'space-between',
								width: '100%',
								mb: '-5px',
							}}
						>
							<AmountAndCurrency
								amount={vatAndEasFee?.totalEASFee ?? 0}
								currency="EUR"
								size={1.5}
								color="primary"
								bold
							/>
							<InfoDialButton
								text={`This is expected EAS fee to be included into Invoice on ${now
									.plus({ month: 1 })
									.set({ day: 5 })
									.toFormat('yyyy-MM-dd')}.`}
							/>
						</Box>
					</Box>
				</Box>
			) : (
				<Box
					sx={{
						display: 'flex',
						flexDirection: 'row',
						gap: 1,
						bgcolor: '#fff',
					}}
				>
					{[0, 1, 2, 3, 4, 5, 6].map((item) => (
						<Skeleton
							key={item}
							variant="rectangular"
							height={60}
							sx={{
								width: '100%',
								borderRadius: '12px',
								'& .MuiCardContent-root': { padding: '8px 16px 4px 16px!important' },
							}}
						/>
					))}
				</Box>
			)}
		</Box>
	);
};
